import { faFolder, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Badge, Tooltip } from '@ui';
import { cx } from 'class-variance-authority';
import { head } from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { BaseComponentProps } from './types';

interface Props extends BaseComponentProps {
    showFirstPosition?: boolean;
}

const openPdf = (e: React.MouseEvent, url: string) => {
    e.preventDefault();
    e.stopPropagation();
    window.open(url, '_blank');
};

export const NumberComponent = React.memo(({ invoice, showFirstPosition }: Props) => {
    const { t } = useTranslation('invoice');
    return (
        <Tooltip
            placement="right"
            className={cx(
                'tw-absolute tw-bottom-2 tw-left-1 tw-right-2 tw-top-2 tw-flex tw-items-center tw-pl-3',
                'before:tw-absolute before:-tw-left-1 before:tw-top-0 before:tw-h-full before:tw-w-1 before:tw-rounded-lg before:tw-content-[""]',
                invoice.isCredit ? 'before:tw-bg-success/40' : 'before:tw-bg-warning/40'
            )}
            tooltip={
                <div className="tw-flex tw-flex-col">
                    <span className="tw-mb-2 tw-font-bold">{t('invoiceList.number.itemsPreview')}</span>
                    {invoice.items?.map((item) => (
                        <div key={item.id} className="tw-flex tw-flex-col">
                            <span className="tw-text-accents-6">{item.description}</span>
                            {item.description_long && (
                                <span className="tw-whitespace-pre-wrap tw-text-accents-5">
                                    {item.description_long}
                                </span>
                            )}
                        </div>
                    ))}
                </div>
            }
        >
            <div className="tw-flex tw-flex-col">
                {invoice.category_title && (
                    <span className="tw-flex tw-items-center tw-gap-2 tw-text-xs tw-font-semibold tw-text-accents-5">
                        <FontAwesomeIcon icon={faFolder} />
                        {invoice.category_title}
                    </span>
                )}
                <span className="tw-font-bold">{invoice.number ? invoice.number : '-'}</span>
                <span className="tw-text-xs tw-italic tw-text-accents-6">
                    {showFirstPosition ? head(invoice.items)?.description : invoice.title}
                </span>
                <div className="tw-mt-1.5 tw-flex tw-flex-wrap tw-gap-1 tw-self-start">
                    {invoice.cancelled_at ? (
                        <Badge size="xs" color="error" flat>{t('invoiceList.number.status.cancelled')}</Badge>
                    ) : invoice.payed_at ? (
                        <Badge size="xs" color="success" flat>{t('invoiceList.number.status.paid')}</Badge>
                    ) : invoice.finalized || invoice.number ? (
                        <Badge size="xs" color="secondary" flat>{t('invoiceList.number.status.finalized')}</Badge>
                    ) : (
                        <Badge size="xs" color="light" flat>{t('invoiceList.number.status.draft')}</Badge>
                    )}
                    {!invoice.cancelled_at &&
                        (invoice.finalized || invoice.number) &&
                        invoice.ledger_posted === false && (
                            <Tooltip tooltip={t('invoiceList.number.notPostedTooltip')}>
                                <Badge size="xs" color="warning" flat>
                                    <FontAwesomeIcon icon={faTriangleExclamation} className="tw-mr-1" />
                                    {t('invoiceList.number.status.notPosted')}
                                </Badge>
                            </Tooltip>
                        )}
                    {invoice.credited_invoice && (
                        <Badge
                            size="xs"
                            color="light"
                            flat
                            className="tw-cursor-pointer"
                            onClick={(e) => openPdf(e, invoice.credited_invoice!.url)}
                        >
                            {t('invoiceList.number.invoiceBadge', { number: invoice.credited_invoice.number ?? '-' })}
                        </Badge>
                    )}
                    {invoice.credit_notes?.map((cn) => (
                        <Badge
                            key={cn.id}
                            size="xs"
                            color="light"
                            flat
                            className="tw-cursor-pointer"
                            onClick={(e) => openPdf(e, cn.url)}
                        >
                            {t('invoiceList.number.creditBadge', { number: cn.number ?? '-' })}
                        </Badge>
                    ))}
                </div>
            </div>
        </Tooltip>
    );
});

NumberComponent.displayName = 'NumberComponent';
